import type React from "react"

import type { RunMetricOutput } from "@/types/RunMetricOutput"

interface DeepDiveSection {
  label: string
  content: React.ReactNode
}

export function appendAgentJudgeEvidence(
  sections: DeepDiveSection[] | null,
  metricOutput: RunMetricOutput,
): DeepDiveSection[] | null {
  const evidenceReferences = getEvidenceReferences(metricOutput)
  if (evidenceReferences.length === 0) {
    return sections
  }
  return [
    ...(sections ?? []),
    {
      label: "Evidence",
      content: (
        <ul className="space-y-1 text-sm text-coval-text">
          {evidenceReferences.map((reference) => (
            <li key={reference.key}>{reference.label}</li>
          ))}
        </ul>
      ),
    },
  ]
}

function getEvidenceReferences(
  metricOutput: RunMetricOutput,
): Array<{ key: string; label: string }> {
  const nestedEvidenceReferences = [
    metricOutput.result,
    metricOutput.result_json,
  ].map((source) => {
    const llm: unknown = source?.llm
    return typeof llm === "object" && llm !== null
      ? (llm as Record<string, unknown>).evidence_references
      : undefined
  })

  for (const references of [
    metricOutput.evidence_references,
    ...nestedEvidenceReferences,
  ]) {
    if (!Array.isArray(references)) {
      continue
    }
    const parsedReferences = references.flatMap(toEvidenceReference)
    if (parsedReferences.length > 0) {
      return parsedReferences
    }
  }
  return []
}

function toEvidenceReference(
  reference: unknown,
): Array<{ key: string; label: string }> {
  if (typeof reference !== "object" || reference === null) {
    return []
  }
  const candidate = reference as Record<string, unknown>
  if (
    (candidate.type === undefined || candidate.type === "frame") &&
    typeof candidate.frame_id === "string"
  ) {
    return [
      {
        key: `frame:${candidate.frame_id}`,
        label: `Frame: ${candidate.frame_id}`,
      },
    ]
  }
  if (
    (candidate.type === undefined || candidate.type === "trace_span") &&
    typeof candidate.trace_id === "string" &&
    typeof candidate.span_id === "string"
  ) {
    return [
      {
        key: `trace:${candidate.trace_id}:${candidate.span_id}`,
        label: `Trace span: ${candidate.trace_id} / ${candidate.span_id}`,
      },
    ]
  }
  return []
}
